import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Illustration from './Illustration';
import { CalendarDaysIcon, MapPinIcon, BookOpenIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next'; // Import i18n hook

const Hero: React.FC = () => {
  const { t } = useTranslation('public'); // Gunakan namespace 'public'
  const navigate = useNavigate();

  const handleMenuClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();

    // Jika tujuannya halaman lain (bukan hash)
    if (!href.startsWith("#")) {
      navigate(href.startsWith("/") ? href : `/${href}`);
      return;
    }

    // Jika saat ini bukan di halaman utama, arahkan ke root lalu scroll
    if (location.pathname !== "/") {
      navigate(`/${href}`);
      return;
    }

    // Scroll halus ke elemen yang dituju
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
      // Ubah URL tanpa reload
      window.history.replaceState(null, "", href);
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-white dark:bg-gray-900 pt-2 pb-12">
      {/* New Background Elements */}
      <div className="absolute inset-0 z-0">
        <svg width="100%" height="100%" className="absolute inset-0">
          <defs>
            <radialGradient id="aura-gradient-light" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(59, 130, 246, 0.15)" />
              <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
            </radialGradient>
            <radialGradient id="aura-gradient-dark" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(30, 58, 138, 0.2)" />
              <stop offset="100%" stopColor="rgba(30, 58, 138, 0)" />
            </radialGradient>

            {/* Pattern Dot Grid: Memberikan kesan struktur yang rapi dan elegan */}
            <pattern id="dot-pattern" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" className="fill-gray-200 dark:fill-gray-800" />
            </pattern>
          </defs>

          {/* Background Pattern Layer */}
          <rect width="100%" height="100%" fill="url(#dot-pattern)" className="opacity-70" />

          <motion.circle
            cx="25%"
            cy="30%"
            r="40%"
            className="fill-[url(#aura-gradient-light)] dark:fill-[url(#aura-gradient-dark)]"
            animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.circle
            cx="75%"
            cy="70%"
            r="35%"
            className="fill-[url(#aura-gradient-light)] dark:fill-[url(#aura-gradient-dark)]"
            animate={{ scale: [1, 0.8, 1], opacity: [0.7, 0.9, 0.7] }}
            transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
          />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">

          <motion.div
            className="relative h-80 md:h-auto md:aspect-square w-full order-2 md:order-1 pt-0 hidden md:block"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <Illustration />
          </motion.div>

          <div className="text-center md:text-left order-1 md:order-2">
            <div className="text-center md:text-left">
              <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 mb-2 sm:mb-0">
                {[
                  "/diktisaintek-berdampak.png",
                  // "/kampus-merdeka.png",
                ].map((logo, i) => (
                  <img
                    key={i}
                    src={logo}
                    alt={`Logo ${i + 1}`}
                    className="h-12 sm:h-14 md:h-17 w-auto object-contain transition-transform duration-300 hover:scale-110"
                  />
                ))}
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold bg-gradient-to-r from-blue-600 to-yellow-300 mb-5 sm:mb-6 leading-tight bg-clip-text text-transparent">
                {t('hero.title')}
              </h1>

              <div className="flex flex-col gap-4 mb-8 sm:mb-10 items-center md:items-start">
                {/* Conference Info */}
                <div className="w-full max-w-xs sm:max-w-sm md:max-w-none space-y-2.5">
                  <div className="flex items-start gap-3 text-left">
                    <CalendarDaysIcon className="w-5 h-5 shrink-0 mt-0.5 text-primary dark:text-light" />
                    <p className="text-sm sm:text-base">
                      <span className="block font-semibold text-gray-900 dark:text-white">{t('hero.conference.type')}</span>
                      <span className="block text-gray-600 dark:text-gray-400">{t('hero.conference.date')}</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3 text-left">
                    <MapPinIcon className="w-5 h-5 shrink-0 mt-0.5 text-primary dark:text-light" />
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">{t('hero.conference.location')}</p>
                  </div>
                </div>

                <hr className="w-full max-w-xs sm:max-w-sm md:max-w-none border-gray-200 dark:border-gray-700" />

                {/* Convention Info */}
                <div className="w-full max-w-xs sm:max-w-sm md:max-w-none space-y-2.5">
                  <div className="flex items-start gap-3 text-left">
                    <CalendarDaysIcon className="w-5 h-5 shrink-0 mt-0.5 text-primary dark:text-light" />
                    <p className="text-sm sm:text-base">
                      <span className="block font-semibold text-gray-900 dark:text-white">{t('hero.convention.type')}</span>
                      <span className="block text-gray-600 dark:text-gray-400">{t('hero.convention.date')}</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3 text-left">
                    <MapPinIcon className="w-5 h-5 shrink-0 mt-0.5 text-primary dark:text-light" />
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">{t('hero.convention.location')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-5 justify-center md:justify-start mb-15 pt-4">

              {/* TOMBOL 1: DAFTAR KONVENSI (Style: Premium Blue Gradient) */}
              <button
                onClick={() => navigate("/registration/convention")}
                className="group relative flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-bold tracking-wide shadow-lg shadow-blue-500/30 overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-blue-500/50 hover:from-blue-500 hover:to-indigo-600"
              >
                {/* Efek Kilau (Shine Effect) lewat CSS murni */}
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />

                <UserPlusIcon className="w-5 h-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:rotate-6" />
                <span className="relative z-10">{t('hero.buttons.register_convention')}</span>
              </button>

              {/* TOMBOL 2: DAFTAR KONFERENSI (Style: Modern Dark/Light Outline dengan Gradient Border) */}
              <a
                href="#conference-tracks"
                onClick={(e) => { handleMenuClick(e, '#conference-tracks'); }}
                role="button"
                className="group relative flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-white dark:bg-gray-800 text-gray-800 dark:text-white font-bold tracking-wide shadow-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-xl ring-2 ring-transparent hover:ring-blue-400 dark:hover:ring-blue-500"
              >
                {/* Background Gradient tipis untuk memberikan nuansa premium */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <BookOpenIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 relative z-10 transition-transform duration-300 group-hover:scale-110" />
                <span className="relative z-10 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">{t('hero.buttons.register_conference')}</span>
              </a>

            </div>
            {/* Tambahkan style untuk animasi shimmer jika belum ada di tailwind config */}
            <style>{`
            @keyframes shimmer {
              100% {
                transform: translateX(100%);
              }
            }
          `}</style>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;